import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Users, ChevronLeft, Share2, Printer, ShoppingBag } from 'lucide-react';
import { recipes } from '../data/recipes';
import { cn } from '../utils/cn';

interface RecipeDetailPageProps {
  recipeId: number;
}

const RecipeDetailPage = ({ recipeId }: RecipeDetailPageProps) => {
  const [recipe, setRecipe] = useState(recipes.find(r => r.id === recipeId));
  const [servings, setServings] = useState(recipe?.servings || 4);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Find the recipe by ID
    const foundRecipe = recipes.find(r => r.id === recipeId);
    
    if (!foundRecipe) {
      // If recipe not found, redirect to recipes page
      navigate('/recipes');
      return;
    }
    
    setRecipe(foundRecipe);
    setServings(foundRecipe.servings);
    
    // Update page title for SEO
    document.title = `${foundRecipe.title} - Egg Timer Online`;
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, [recipeId, navigate]);
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: recipe?.title,
        text: recipe?.description,
        url: window.location.href,
      })
      .catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href)
        .then(() => alert('Link copied to clipboard!'))
        .catch((error) => console.log('Error copying to clipboard', error));
    }
  };
  
  if (!recipe) {
    return null; // Will redirect in useEffect
  }
  
  // Calculate adjusted ingredients based on servings
  const getAdjustedIngredient = (ingredient: string) => {
    // Try to find a number at the beginning of the ingredient
    const match = ingredient.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
    
    if (match) {
      const originalAmount = parseFloat(match[1]);
      const unit = match[2];
      const adjustmentFactor = servings / recipe.servings;
      const adjustedAmount = (originalAmount * adjustmentFactor).toFixed(1).replace(/\.0$/, '');
      return `${adjustedAmount} ${unit}`;
    }
    
    return ingredient;
  };
  
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mb-8">
        <Link to="/recipes" className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Recipes
        </Link>
      </div>
      
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            {recipe.title}
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            {recipe.description}
          </p>
          
          <div className="flex flex-wrap gap-3 mb-6">
            {recipe.keywords.map((keyword, idx) => (
              <span key={idx} className="text-sm bg-primary-50 text-primary-700 px-3 py-1 rounded-full">
                {keyword}
              </span>
            ))}
          </div>
          
          <div className="flex flex-wrap gap-6 mb-8">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-primary-500 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Prep Time</p>
                <p className="font-medium">{recipe.prepTime}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-primary-500 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Cook Time</p>
                <p className="font-medium">{recipe.cookTime}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <Users className="w-5 h-5 text-primary-500 mr-2" />
              <div>
                <p className="text-sm text-gray-500">Servings</p>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setServings(Math.max(1, servings - 1))}
                    className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
                    aria-label="Decrease servings"
                  >
                    <span>-</span>
                  </button>
                  <span className="font-medium">{servings}</span>
                  <button 
                    onClick={() => setServings(servings + 1)}
                    className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
                    aria-label="Increase servings"
                  >
                    <span>+</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="relative mb-8 rounded-xl overflow-hidden h-80 md:h-96">
          <img 
            src={recipe.image} 
            alt={recipe.title} 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="flex justify-center gap-4 mb-10">
          <button
            onClick={handleShare}
            className="flex items-center px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <Share2 className="w-4 h-4 mr-2" />
            <span>Share</span>
          </button>
          
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <Printer className="w-4 h-4 mr-2" />
            <span>Print</span>
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div className="md:col-span-1">
            <div className="sticky top-24">
              <h2 className="font-display text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                Ingredients
              </h2>
              <ul className="space-y-2 mb-6">
                {recipe.ingredients.map((ingredient, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="flex-shrink-0 w-5 h-5 mt-1 mr-2 border border-primary-400 rounded-full"></span>
                    <span>
                      {ingredient.startsWith('For') 
                        ? ingredient 
                        : getAdjustedIngredient(ingredient)
                      }
                    </span>
                  </li>
                ))}
              </ul>
              
              {recipe.amazonProducts && (
                <div className="bg-primary-50 rounded-lg p-4 mt-6">
                  <h3 className="font-display text-lg font-semibold text-gray-800 mb-3">
                    Recommended Tools
                  </h3>
                  <ul className="space-y-4">
                    {recipe.amazonProducts.map((product, idx) => (
                      <li key={idx} className="border-b border-primary-100 last:border-none pb-3 last:pb-0">
                        <h4 className="font-medium text-gray-800 mb-1">{product.title}</h4>
                        <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-primary-700 font-medium">{product.price}</span>
                          <a 
                            href={product.link} 
                            target="_blank" 
                            rel="nofollow noopener noreferrer"
                            className="inline-flex items-center text-xs bg-primary-500 text-white px-3 py-1 rounded-full hover:bg-primary-600 transition-colors"
                          >
                            <ShoppingBag className="w-3 h-3 mr-1" />
                            View on Amazon
                          </a>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          
          <div className="md:col-span-2">
            <h2 className="font-display text-xl font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">
              Instructions
            </h2>
            <ol className="space-y-6 mb-8">
              {recipe.instructions.map((instruction, idx) => (
                <li key={idx} className="flex">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-medium mr-4 mt-1">
                    {idx + 1}
                  </span>
                  <div className="text-gray-700">
                    {instruction}
                  </div>
                </li>
              ))}
            </ol>
            
            {recipe.tips && (
              <div className="bg-primary-50 border-l-4 border-primary-400 p-4 rounded-r-lg mb-8">
                <h3 className="font-display text-lg font-semibold text-gray-800 mb-2">
                  Pro Tip
                </h3>
                <p className="text-gray-700">
                  {recipe.tips}
                </p>
              </div>
            )}
            
            <h3 className="font-display text-xl font-semibold text-gray-800 mb-4">
              Related Recipes
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {recipes
                .filter(r => r.id !== recipe.id)
                .filter(r => 
                  r.keywords.some(keyword => 
                    recipe.keywords.includes(keyword)
                  )
                )
                .slice(0, 2)
                .map(relatedRecipe => (
                  <Link
                    key={relatedRecipe.id}
                    to={`/recipes/${relatedRecipe.slug}`}
                    className="flex items-center bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="w-24 h-24 flex-shrink-0">
                      <img 
                        src={relatedRecipe.image} 
                        alt={relatedRecipe.title} 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-medium text-gray-800 mb-1 line-clamp-1">
                        {relatedRecipe.title}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {relatedRecipe.description}
                      </p>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailPage;